/**
 * "Add DSCR pricing" wizard — the six steps from
 * guidelines-and-dscr-pricing-admin.md:
 *
 *   1 Counterparty        → the identity + contact fields
 *   2 Source sheet upload → POST /dscr-pricing/import with store=false
 *                           (parses + runs the overlay agent, stores NOTHING)
 *                           + asOfDate: the sheet DAY it reflects (default
 *                           today, Eastern) — daily sheet tracking
 *   3 Field mapping       → parsed.fieldOptions
 *   4 Grids / LLPAs       → parsed.ladders + parsed.llpaTables
 *   5 Rule checks         → parsed.guidelineOverlays (editable)
 *   6 Review & save       → PUT /dscr-pricing/{cpId} with the reviewed objects
 *
 * Staging first is the point: the agent's overlay mapping is a suggestion, and
 * nothing reaches the versioned store until step 6.
 *
 * Shell: the same `QuickEditDialog` + FormPrimitives the RTL "Add tier" editor
 * uses, widened to 880 — the step name rides in the title's description line,
 * Back sits in the footer's left slot, the primary is Next / Parse / Save.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import QuickEditDialog from "../../../components/forms/QuickEditDialog";
import {
  FormCheckList,
  FormCheckRow,
  FormDateField,
  FormFieldGrid,
  FormFileDrop,
  FormNote,
  FormSelectField,
  FormTextField,
} from "../../../components/forms/FormPrimitives";
import { FORM, px } from "../../../theme/formStyles";
import { adminDscrPricingApi } from "../../../api/adminDataApi";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import { AdminEmpty } from "../shared/AdminScreenShell";
import GridAgentReport from "./GridAgentReport";

const STEPS = [
  "Counterparty",
  "Source sheet",
  "Field mapping",
  "Grids & LLPAs",
  "Rule checks",
  "Review & save",
];

const STATUSES = ["draft", "active", "archived"];

const XLSX_ACCEPT =
  ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// The desk's day (Eastern), not the browser's and not the server's UTC day —
// after 8 PM Eastern the backend's own default would already be tomorrow.
const todayEastern = () => formatDateTime(new Date().toISOString(), "yyyy-MM-dd");

const isSheetDay = (v) =>
  /^\d{4}-\d{2}-\d{2}$/.test(v || "") && v <= todayEastern();

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export default function DscrImportWizard({ open, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [counterparty, setCounterparty] = useState("");
  const [cpId, setCpId] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [status, setStatus] = useState("draft");
  const [notify, setNotify] = useState(false);

  // FormFileDrop's value shape — [{ id, file, name, … }], one workbook.
  const [fileEntries, setFileEntries] = useState([]);
  const file = fileEntries[0]?.file || null;
  const [asOfDate, setAsOfDate] = useState(todayEastern);
  const [staged, setStaged] = useState(null); // the import response
  const [pricing, setPricing] = useState(null); // the editable parsed objects

  useEffect(() => {
    if (open) return;
    setStep(0);
    setBusy(false);
    setCounterparty("");
    setCpId("");
    setContactName("");
    setContactEmail("");
    setStatus("draft");
    setNotify(false);
    setFileEntries([]);
    setAsOfDate(todayEastern());
    setStaged(null);
    setPricing(null);
  }, [open]);

  const effectiveCpId = cpId.trim() || slugify(counterparty);

  const parseSheet = useCallback(() => {
    if (!file || !counterparty.trim()) return;
    setBusy(true);
    adminDscrPricingApi
      .import(file, {
        counterparty: counterparty.trim(),
        cpId: cpId.trim() || undefined,
        contactName: contactName.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        status,
        asOfDate,
        store: false, // stage only — step 6 commits
      })
      .then((res) => {
        setStaged(res);
        setPricing(res?.parsed || null);
        setStep(2);
      })
      .catch((err) =>
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't parse the workbook",
          { variant: "error" },
        ),
      )
      .finally(() => setBusy(false));
  }, [
    file,
    counterparty,
    cpId,
    contactName,
    contactEmail,
    status,
    asOfDate,
    enqueueSnackbar,
  ]);

  const commit = useCallback(() => {
    if (!pricing) return;
    const id = staged?.cpId || effectiveCpId;
    setBusy(true);
    adminDscrPricingApi
      .save(id, {
        pricing,
        counterparty: counterparty.trim(),
        status,
        notify,
        // The sheet day rides on the commit too: this PUT is what writes the
        // version, and the import above only staged it.
        asOfDate,
        note: `imported ${staged?.sourceFile || "workbook"}`,
      })
      .then((out) => {
        enqueueSnackbar(`${counterparty.trim()} saved as v${out?.version}`, {
          variant: "success",
        });
        onSaved?.(id);
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.detail || "Save failed", {
          variant: "error",
        }),
      )
      .finally(() => setBusy(false));
  }, [
    pricing,
    staged,
    effectiveCpId,
    counterparty,
    status,
    notify,
    asOfDate,
    enqueueSnackbar,
    onSaved,
  ]);

  const canAdvance = useMemo(() => {
    if (step === 0) return !!counterparty.trim();
    if (step === 1) return !!file && isSheetDay(asOfDate);
    return !!pricing;
  }, [step, counterparty, file, asOfDate, pricing]);

  const next = useCallback(() => {
    if (step === 1) return parseSheet();
    if (step === STEPS.length - 1) return commit();
    setStep((s) => s + 1);
  }, [step, parseSheet, commit]);

  const primaryText =
    step === 1 ? "Parse workbook" : step === STEPS.length - 1 ? "Save" : "Next";

  return (
    <QuickEditDialog
      open={open}
      title="Add DSCR pricing"
      description={`Step ${step + 1} of ${STEPS.length} · ${STEPS[step]}`}
      onClose={onClose}
      onSave={next}
      saveText={primaryText}
      canSave={canAdvance}
      saving={busy}
      enterHint={false}
      hideFieldLabel={false}
      width={880}
      // Steps 3–5 run tall (grids, overlay rows): the field area scrolls
      // between the pinned header and footer.
      bodySx={{
        pt: px(FORM.s.s5),
        overflowY: "auto",
        minHeight: 0,
        flex: "1 1 auto",
      }}
      status={
        step > 0 ? (
          <Button
            type="button"
            variant="text"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={busy}
          >
            Back
          </Button>
        ) : null
      }
    >
      <Stepper activeStep={step}>
        {STEPS.map((s) => (
          <Step key={s}>
            <StepLabel>
              <Txt sx={{ fontSize: 12 }}>{s}</Txt>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {busy ? <LinearProgress /> : null}

      {step === 0 ? (
        <FormFieldGrid>
          <FormTextField
            span={6}
            label="Counterparty"
            required
            value={counterparty}
            onChange={setCounterparty}
          />
          <FormTextField
            span={6}
            label="Counterparty id"
            value={cpId}
            onChange={setCpId}
            placeholder={slugify(counterparty)}
            helperText="Blank derives it from the name. An existing id adds a new version to that sheet."
          />
          <FormTextField
            span={3}
            label="Contact name"
            value={contactName}
            onChange={setContactName}
          />
          <FormTextField
            span={3}
            label="Contact email"
            value={contactEmail}
            onChange={setContactEmail}
          />
          <FormSelectField
            span={3}
            label="Status"
            value={status}
            options={STATUSES}
            onChange={(v) => setStatus(v || "draft")}
          />
        </FormFieldGrid>
      ) : null}

      {step === 1 ? (
        <FormFieldGrid>
          <FormFileDrop
            span={6}
            label="Source workbook"
            required
            accept={XLSX_ACCEPT}
            value={fileEntries}
            onChange={setFileEntries}
            hint=".xlsx price sheet"
          />
          <FormDateField
            span={3}
            label="Sheet as of"
            required
            value={asOfDate}
            onChange={(v) => setAsOfDate(v || "")}
            disabled={busy}
            helperText="The day this sheet's pricing reflects. One upload per counterparty per day."
          />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <FormNote>
              Continuing parses the workbook and runs the pricing-sheet agent to
              map guideline overlays. Nothing is stored until the final step —
              the original grid is kept as source.xlsx when you save.
            </FormNote>
          </Box>
        </FormFieldGrid>
      ) : null}

      {step === 2 ? (
        <Flx column g={2}>
          {/* The agent's read of the uploaded workbook, surfaced at the first
              review step — a layout change gets caught here, not after it's
              stored. */}
          <GridAgentReport gridAgent={pricing?.gridAgent} />
          <FieldOptionsStep options={pricing?.fieldOptions} />
        </Flx>
      ) : null}

      {step === 3 ? <GridsStep pricing={pricing} /> : null}

      {step === 4 ? (
        <OverlaysStep
          pricing={pricing}
          onChange={(overlays) =>
            setPricing((prev) => ({ ...prev, guidelineOverlays: overlays }))
          }
        />
      ) : null}

      {step === 5 ? (
        <Flx column g={2.5}>
          <Flx g={4} wrap>
            <Summary label="Counterparty" value={counterparty} />
            <Summary label="Id" value={staged?.cpId || effectiveCpId} />
            <Summary label="Source file" value={staged?.sourceFile || "—"} />
            <Summary
              label="Sheet as of"
              value={formatDate(asOfDate, "—", "MMM d, yyyy")}
            />
            <Summary label="Status" value={status} />
            <Summary
              label="Tiers"
              value={String(pricing?.tierMargins?.length ?? 0)}
            />
            <Summary
              label="Ladders"
              value={String(Object.keys(pricing?.ladders || {}).length)}
            />
            <Summary
              label="LLPA tables"
              value={String(pricing?.llpaTables?.length ?? 0)}
            />
            <Summary
              label="Overlays"
              value={String(pricing?.guidelineOverlays?.length ?? 0)}
            />
          </Flx>
          {pricing?.unparsed?.length ? (
            <Txt sx={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>
              {pricing.unparsed.length} unparsed row
              {pricing.unparsed.length === 1 ? "" : "s"} — these did not map to
              a known grid and will be stored as-is.
            </Txt>
          ) : null}
          <FormCheckList>
            <FormCheckRow
              title="Notify on save"
              subtext="Admin, capital markets and underwriting get a notice of the new version."
              value={notify}
              onChange={setNotify}
            />
          </FormCheckList>
        </Flx>
      ) : null}
    </QuickEditDialog>
  );
}

DscrImportWizard.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
};

const Summary = ({ label, value }) => (
  <Flx column g={0.25}>
    <Txt sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{label}</Txt>
    <Txt sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
      {value || "—"}
    </Txt>
  </Flx>
);

/** Step 3 — what the parser recognised: tiers, ladder columns, LLPA row labels. */
const FieldOptionsStep = ({ options }) => {
  if (!options) return <AdminEmpty>No field options returned.</AdminEmpty>;
  return (
    <Flx column g={2.5}>
      <ChipRow label="Tiers" items={options.tiers} />
      <ChipRow label="Ladder columns" items={options.ladderColumns} />
      <Box>
        <Txt sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", display: "block", mb: 1 }}>
          LLPA tables
        </Txt>
        {(options.llpaTables || []).map((t) => (
          <Box key={t.table} sx={{ mb: 1.5 }}>
            <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: "#334155", display: "block" }}>
              {t.table}
            </Txt>
            <Flx g={0.5} wrap sx={{ mt: 0.5 }}>
              {(t.rowLabels || []).map((l) => (
                <Chip key={l} size="small" label={l} sx={{ height: 20, fontSize: 11 }} />
              ))}
            </Flx>
          </Box>
        ))}
      </Box>
    </Flx>
  );
};

const ChipRow = ({ label, items }) => (
  <Box>
    <Txt sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", display: "block", mb: 1 }}>
      {label}
    </Txt>
    <Flx g={0.5} wrap>
      {(items || []).length ? (
        items.map((i) => (
          <Chip key={i} size="small" label={i} sx={{ height: 22, fontSize: 11 }} />
        ))
      ) : (
        <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>none</Txt>
      )}
    </Flx>
  </Box>
);

/** Step 4 — a read-only look at what parsed, before the overlay review. */
const GridsStep = ({ pricing }) => (
  <Flx column g={2}>
    <Flx g={4} wrap>
      <Summary label="Tier margins" value={String(pricing?.tierMargins?.length ?? 0)} />
      {Object.entries(pricing?.ladders || {}).map(([tier, rows]) => (
        <Summary key={tier} label={`${tier} ladder`} value={`${rows.length} coupons`} />
      ))}
      <Summary label="LLPA tables" value={String(pricing?.llpaTables?.length ?? 0)} />
    </Flx>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        maxHeight: 340,
        overflow: "auto",
        bgcolor: "#f8fafc",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        fontSize: 11.5,
      }}
    >
      {JSON.stringify(
        {
          tierMargins: pricing?.tierMargins,
          ladders: pricing?.ladders,
          llpaTables: pricing?.llpaTables,
        },
        null,
        2,
      )}
    </Box>
    <FormNote>
      Grids are editable cell-by-cell on the counterparty page once saved.
    </FormNote>
  </Flx>
);

/** Step 5 — the agent's overlay suggestions, editable / droppable before save. */
const OverlaysStep = ({ pricing, onChange }) => {
  const overlays = pricing?.guidelineOverlays || [];
  const set = (i, patch) => {
    const next = overlays.slice();
    next[i] = { ...overlays[i], ...patch };
    onChange(next);
  };
  if (!overlays.length)
    return (
      <AdminEmpty>
        The agent mapped no guideline overlays for this sheet.
      </AdminEmpty>
    );
  return (
    <Flx column g={1.5}>
      {pricing?.overlayAgent?.notes ? (
        <FormNote>{pricing.overlayAgent.notes}</FormNote>
      ) : null}
      {overlays.map((o, i) => (
        <Box
          key={i}
          sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: "8px" }}
        >
          <FormFieldGrid>
            <FormTextField
              span={6}
              label="Rule"
              multiline
              value={o.rule}
              onChange={(v) => set(i, { rule: v })}
            />
            <FormTextField
              span={3}
              label="Kind"
              value={o.kind}
              onChange={(v) => set(i, { kind: v })}
            />
            <FormTextField
              span={3}
              label="Suggested field"
              value={o.suggestedField}
              onChange={(v) => set(i, { suggestedField: v })}
            />
          </FormFieldGrid>
          <Flx sx={{ justifyContent: "flex-end", mt: 1 }}>
            <Button
              type="button"
              variant="text"
              size="small"
              onClick={() => onChange(overlays.filter((_, j) => j !== i))}
            >
              Drop
            </Button>
          </Flx>
        </Box>
      ))}
    </Flx>
  );
};
